import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Check, Send, Loader2, ChevronRight, Shield, Home, User, UserPlus, Mail, Phone } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from "@/components/ui/badge";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════════════
// GUEST INQUIRY FORM COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const GuestInquiryForm = ({ onBack }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredContact: '',
    budgetRange: '',
    timeline: '',
    message: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: 'Private Sessions — Guest Inquiry',
          message: formData.message,
          source: 'private_sessions_guest',
          phone: formData.phone || null,
          preferred_contact: formData.preferredContact || null,
          budget_range: formData.budgetRange || null,
          timeline: formData.timeline || null
        })
      });
      
      if (response.status === 429) {
        throw new Error('You\'ve submitted too many requests. Please wait a few minutes and try again.');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit inquiry');
      }
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting guest inquiry:', error);
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success State
  if (isSubmitted) {
    return (
      <div className="flex flex-col min-h-screen bg-[#050816]">
        <section className="flex-grow flex items-center justify-center px-6 py-20">
          <div className="text-center max-w-[500px]">
            <div className="w-16 h-16 rounded-full bg-[#C6A14A]/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#C6A14A]" />
            </div>
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              Inquiry Received
            </p>
            <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Thank You
            </h1>
            <p className="text-white/50 leading-relaxed mb-8">
              Your private consultation request has been received. I'll personally review your inquiry and reach out within 24-48 hours to discuss your vision.
            </p>
            
            {/* CTA to create account */}
            <div className="space-y-4">
              <Link 
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#C6A14A] text-[#0f1724] font-semibold hover:bg-[#C6A14A]/90 transition-all w-full justify-center"
                data-testid="guest-success-register-btn"
              >
                <UserPlus className="w-4 h-4" />
                Create Account to Track Requests
              </Link>
              <Link 
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-white/70 font-medium hover:bg-white/5 transition-all w-full justify-center"
              >
                <Home className="w-4 h-4" />
                Return Home
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#050816]">
      {/* Hero Section */}
      <section className="hero-main relative min-h-[45vh] md:min-h-[50vh] flex items-end justify-center overflow-hidden pb-16 md:pb-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1586867737814-a2db8b13de38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxyb21hbnRpYyUyMGNvdXBsZSUyMHNpbGhvdWV0dGUlMjBpbnRpbWF0ZSUyMGJvdWRvaXIlMjBhcnRpc3RpY3xlbnwwfHx8fDE3NzA5NTMzNzd8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050816]/50 via-[#050816]/60 to-[#050816]" />
        
        <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C6A14A]/20 border border-[#C6A14A]/30 mb-6">
            <User className="w-3 h-3 text-[#C6A14A]" />
            <span className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase">Guest Inquiry</span>
          </div>
          
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Private Consultation
          </h1>
          
          <p className="text-white/60 text-lg leading-relaxed">
            Share your vision with complete discretion. No account required.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="relative -mt-12 pb-20 px-6">
        <div className="max-w-xl mx-auto">
          <div className="bg-[#0f1724] rounded-2xl p-8 md:p-10 border border-white/5 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-8 h-px bg-[#C6A14A]/30" />
                <Lock className="w-4 h-4 text-[#C6A14A]" />
                <div className="w-8 h-px bg-[#C6A14A]/30" />
              </div>
              <h2 className="text-white text-sm font-semibold tracking-[0.2em] uppercase mb-1">
                Discreet & Confidential
              </h2>
              <p className="text-white/40 text-xs">Your privacy is our priority</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Full Name <span className="text-[#C6A14A]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Your full name"
                  required
                  data-testid="guest-name-input"
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Email <span className="text-[#C6A14A]">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="guest-email-input"
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all"
                />
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Phone <span className="text-white/30 text-xs font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  data-testid="guest-phone-input"
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all"
                />
              </div>

              {/* Preferred Contact Method */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-3">
                  Preferred Contact Method <span className="text-white/30 text-xs font-normal">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'email', label: 'Email', icon: Mail },
                    { value: 'phone', label: 'Phone', icon: Phone }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleInputChange('preferredContact', value)}
                      data-testid={`guest-contact-${value}`}
                      className={cn(
                        "p-3 rounded-xl border text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2",
                        formData.preferredContact === value
                          ? "border-[#C6A14A] bg-[#C6A14A]/10 text-white"
                          : "border-white/10 bg-[#1a2636] hover:border-[#C6A14A]/30 text-white/60 hover:text-white"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget Range */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Budget Range <span className="text-white/30 text-xs font-normal">(optional)</span>
                </label>
                <select
                  value={formData.budgetRange}
                  onChange={(e) => handleInputChange('budgetRange', e.target.value)}
                  data-testid="guest-budget-select"
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#1a2636]">Select a range...</option>
                  <option value="under-500" className="bg-[#1a2636]">Under $500</option>
                  <option value="500-1000" className="bg-[#1a2636]">$500 - $1,000</option>
                  <option value="1000-2500" className="bg-[#1a2636]">$1,000 - $2,500</option>
                  <option value="2500-5000" className="bg-[#1a2636]">$2,500 - $5,000</option>
                  <option value="5000-plus" className="bg-[#1a2636]">$5,000+</option>
                  <option value="flexible" className="bg-[#1a2636]">Flexible / Undecided</option>
                </select>
              </div>

              {/* Timeline */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Timeline <span className="text-white/30 text-xs font-normal">(optional)</span>
                </label>
                <select
                  value={formData.timeline}
                  onChange={(e) => handleInputChange('timeline', e.target.value)}
                  data-testid="guest-timeline-select"
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#1a2636]">Select a timeline...</option>
                  <option value="asap" className="bg-[#1a2636]">As soon as possible</option>
                  <option value="1-2-weeks" className="bg-[#1a2636]">Within 1-2 weeks</option>
                  <option value="1-month" className="bg-[#1a2636]">Within a month</option>
                  <option value="2-3-months" className="bg-[#1a2636]">2-3 months</option>
                  <option value="flexible" className="bg-[#1a2636]">Flexible / Planning ahead</option>
                </select>
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Tell us about your vision <span className="text-[#C6A14A]">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder="Share any details about what you're envisioning, preferred aesthetic, or questions you have..."
                  rows={4}
                  required
                  minLength={10}
                  data-testid="guest-message-input"
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all resize-none"
                />
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <p className="text-red-400 text-sm">{submitError}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!formData.name || !formData.email || !formData.message || formData.message.length < 10 || isSubmitting}
                data-testid="guest-submit-btn"
                className={cn(
                  "w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold transition-all duration-300",
                  (!formData.name || !formData.email || !formData.message || formData.message.length < 10 || isSubmitting)
                    ? "bg-white/10 text-white/30 cursor-not-allowed"
                    : "bg-[#C6A14A] text-[#0f1724] hover:bg-[#C6A14A]/90 shadow-lg hover:shadow-xl"
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Submit Inquiry</span>
                  </>
                )}
              </button>
            </form>

            {/* Back link */}
            <div className="text-center mt-6">
              <button 
                onClick={onBack}
                className="inline-flex items-center gap-2 text-white/40 text-sm hover:text-[#C6A14A] transition-colors"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Back to options
              </button>
            </div>

            {/* Privacy Note */}
            <p className="text-center text-white/30 text-xs mt-4">
              Your privacy is paramount. All communications are confidential.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SECURE GATE COMPONENT - Shows for unauthenticated users
// ═══════════════════════════════════════════════════════════════════════════
const SecureGate = ({ onGuestInquiry }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const navigate = useNavigate();

  const handleSignIn = () => {
    navigate('/login', { state: { from: { pathname: '/services/private-sessions' } } });
  };

  const handleGuestInquiry = () => {
    if (acknowledged) {
      onGuestInquiry();
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#050816]">
      {/* Hero Background */}
      <section className="hero-main relative min-h-[45vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1586867737814-a2db8b13de38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxyb21hbnRpYyUyMGNvdXBsZSUyMHNpbGhvdWV0dGUlMjBpbnRpbWF0ZSUyMGJvdWRvaXIlMjBhcnRpc3RpY3xlbnwwfHx8fDE3NzA5NTMzNzd8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050816]/60 via-[#050816]/70 to-[#050816]" />
      </section>

      {/* Gate Card */}
      <section className="relative -mt-24 pb-20 px-6 flex-grow flex items-start justify-center">
        <div className="max-w-md w-full">
          <div className="bg-[#0f1724] rounded-2xl p-8 md:p-10 border border-white/10 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#C6A14A]/10 border border-[#C6A14A]/20 flex items-center justify-center mx-auto mb-5">
                <Shield className="w-7 h-7 text-[#C6A14A]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-white mb-3">
                Private & Confidential
              </h2>
              <p className="text-white/50 text-sm leading-relaxed">
                This is a secure inquiry area for private consultation requests.
              </p>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-white/10"></div>
              <Lock className="w-4 h-4 text-[#C6A14A]/50" />
              <div className="flex-1 h-px bg-white/10"></div>
            </div>

            {/* Acknowledgment Checkbox */}
            <div className="mb-6">
              <label 
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="secure-gate-acknowledgment"
              >
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={setAcknowledged}
                  className="mt-0.5 border-white/20 data-[state=checked]:bg-[#C6A14A] data-[state=checked]:border-[#C6A14A]"
                />
                <span className="text-sm text-white/70 leading-relaxed group-hover:text-white/90 transition-colors">
                  I understand this is a private, secure inquiry area for confidential consultation requests.
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Primary: Sign In */}
              <Button
                onClick={handleSignIn}
                disabled={!acknowledged}
                className={cn(
                  "w-full py-4 rounded-xl font-semibold transition-all duration-300",
                  acknowledged
                    ? "bg-[#C6A14A] text-[#0f1724] hover:bg-[#C6A14A]/90 shadow-lg hover:shadow-xl"
                    : "bg-white/10 text-white/30 cursor-not-allowed"
                )}
                data-testid="secure-gate-signin-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Sign In to Continue
              </Button>

              {/* Secondary: Guest Inquiry */}
              <Button
                onClick={handleGuestInquiry}
                disabled={!acknowledged}
                variant="outline"
                className={cn(
                  "w-full py-4 rounded-xl font-semibold transition-all duration-300 border",
                  acknowledged
                    ? "border-white/20 text-white hover:bg-white/5 hover:border-white/30"
                    : "border-white/10 text-white/30 cursor-not-allowed"
                )}
                data-testid="secure-gate-guest-btn"
              >
                <User className="w-4 h-4 mr-2" />
                Continue as Guest
              </Button>
            </div>

            {/* Helper text */}
            <p className="text-center text-white/40 text-xs mt-4">
              Guest inquiries are private. You can create an account later if you'd like.
            </p>

            {/* Back Link */}
            <div className="text-center mt-6">
              <Link 
                to="/"
                className="inline-flex items-center gap-2 text-white/40 text-sm hover:text-[#C6A14A] transition-colors"
              >
                <Home className="w-4 h-4" />
                Return Home
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer gradient */}
      <div className="h-16 bg-gradient-to-b from-[#050816] to-[#050816]"></div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CONSULTATION MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const ConsultationModal = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    sessionType: '',
    message: ''
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: 'Private Sessions — Consultation Request',
          message: `Session Type: ${formData.sessionType || 'Not specified'}\nPhone: ${formData.phone || 'Not provided'}\n\n${formData.message}`,
          source: 'private_sessions_authenticated',
          phone: formData.phone || null
        })
      });
      
      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a few minutes.');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit');
      }
      
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(error.message || 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0f1724] rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl">
        {isSubmitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-[#C6A14A]/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#C6A14A]" />
            </div>
            <h3 className="font-serif text-2xl text-white mb-3">Thank You</h3>
            <p className="text-white/60 mb-6">Your consultation request has been received. We'll be in touch within 24-48 hours.</p>
            <Button onClick={onClose} className="bg-[#C6A14A] text-[#0f1724] hover:bg-[#C6A14A]/90">
              Close
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-8 h-px bg-[#C6A14A]/30" />
                <Lock className="w-4 h-4 text-[#C6A14A]" />
                <div className="w-8 h-px bg-[#C6A14A]/30" />
              </div>
              <h2 className="font-serif text-2xl text-white mb-2">Private Consultation</h2>
              <p className="text-white/50 text-sm">All inquiries are confidential</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Session Interest</label>
                <select
                  value={formData.sessionType}
                  onChange={(e) => handleInputChange('sessionType', e.target.value)}
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#C6A14A]/50"
                >
                  <option value="">Select type...</option>
                  <option value="Portrait">Portrait Session</option>
                  <option value="Couples">Couples & Intimate</option>
                  <option value="Conceptual">Conceptual & Editorial</option>
                  <option value="Wedding">Private Wedding</option>
                </select>
              </div>

              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-[#1a2636] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 resize-none"
                  placeholder="Tell us about your vision..."
                />
              </div>

              {submitError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <p className="text-red-400 text-sm">{submitError}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!formData.name || !formData.email || isSubmitting}
                  className="flex-1 bg-[#C6A14A] text-[#0f1724] hover:bg-[#C6A14A]/90 disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BREADCRUMB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const Breadcrumb = () => (
  <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
    <Link to="/" className="text-white/40 hover:text-[#C6A14A] transition-colors">
      Home
    </Link>
    <ChevronRight className="w-3 h-3 text-white/30" />
    <span className="text-white/40">Explore</span>
    <ChevronRight className="w-3 h-3 text-white/30" />
    <span className="text-[#C6A14A]">Private Sessions</span>
  </nav>
);

// ═══════════════════════════════════════════════════════════════════════════
// CHECK ICON COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 3L4.5 8.5L2 6" stroke="#050816" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT - FULL CONTENT PAGE
// ═══════════════════════════════════════════════════════════════════════════
const PrivateSessions = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  // Show Guest Inquiry Form if selected
  if (!isAuthenticated && showGuestForm) {
    return <GuestInquiryForm onBack={() => setShowGuestForm(false)} />;
  }

  // Show Secure Gate for unauthenticated users
  if (!isAuthenticated) {
    return <SecureGate onGuestInquiry={() => setShowGuestForm(true)} />;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTHENTICATED VIEW - FULL CONTENT PAGE
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col min-h-screen bg-[#050816]">
      {/* Consultation Modal */}
      <ConsultationModal 
        isOpen={showConsultationModal} 
        onClose={() => setShowConsultationModal(false)} 
      />

      {/* Hero / Intro Section */}
      <section className="hero-main relative min-h-[55vh] md:min-h-[60vh] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1586867737814-a2db8b13de38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxyb21hbnRpYyUyMGNvdXBsZSUyMHNpbGhvdWV0dGUlMjBpbnRpbWF0ZSUyMGJvdWRvaXIlMjBhcnRpc3RpY3xlbnwwfHx8fDE3NzA5NTMzNzd8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050816]/50 via-[#050816]/60 to-[#050816]" />
      </section>

      {/* Breadcrumb Section */}
      <section className="bg-[#050816] border-b border-white/5">
        <div className="container mx-auto px-6 max-w-[1200px] pt-4 pb-3">
          <Breadcrumb />
        </div>
      </section>

      {/* Philosophy & Expectation - The Experience */}
      <section className="pt-10 pb-6 md:pt-12 md:pb-8 bg-[#050816] text-white">
        <div className="container mx-auto px-6 max-w-[800px] text-center">
          <div className="space-y-6">
            <div className="space-y-4">
              <h2 className="font-serif text-3xl md:text-4xl text-white">The Experience</h2>
              <p className="text-gray-400 text-lg leading-relaxed font-light">
                Creating personal imagery requires a foundation of absolute comfort and professionalism. My approach is patient, communicative, and centered entirely on your boundaries and vision.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 text-left border-t border-white/10 pt-6">
              <div>
                <h3 className="text-[#C6A14A] font-medium mb-3">Collaboration</h3>
                <p className="text-sm text-gray-500">We plan every detail together, from mood and lighting to wardrobe and posing.</p>
              </div>
              <div>
                <h3 className="text-[#C6A14A] font-medium mb-3">Privacy</h3>
                <p className="text-sm text-gray-500">Your privacy is paramount. Images are shared or published only with explicit, written consent.</p>
              </div>
              <div>
                <h3 className="text-[#C6A14A] font-medium mb-3">Comfort</h3>
                <p className="text-sm text-gray-500">A relaxed, pressure-free environment where you set the pace and tone.</p>
              </div>
            </div>

            <div className="pt-2">
              <Button 
                onClick={() => setShowConsultationModal(true)}
                className="bg-white/10 text-white hover:bg-white/20 border border-white/20 rounded-full px-8 py-6 text-base font-medium transition-all"
                data-testid="private-consultation-btn-top"
              >
                Request a Private Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Gallery */}
      <section className="py-8 md:py-10 bg-[#050816]">
        <div className="container mx-auto px-8 max-w-[960px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {/* Image 1 - Black Lingerie */}
            <div className="relative aspect-[3/4] overflow-hidden rounded-2xl group shadow-xl shadow-black/30">
              <img 
                src="https://customer-assets.emergentagent.com/job_76fd0c54-e800-404c-90b2-29f2df9a4bf5/artifacts/w8s4w3cz_z-image_00023_.jpg" 
                alt="Artistic Boudoir Portrait - Black Lace" 
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
            
            {/* Image 2 - Teal Bodysuit (Offset) */}
            <div className="relative aspect-[3/4] overflow-hidden rounded-2xl group md:mt-12 lg:mt-16 shadow-xl shadow-black/30">
              <img 
                src="https://customer-assets.emergentagent.com/job_76fd0c54-e800-404c-90b2-29f2df9a4bf5/artifacts/9rlqnjcg_Snip20260204_65.jpg" 
                alt="Artistic Boudoir Portrait - Teal" 
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>

            {/* Image 3 - Emerald Back View */}
            <div className="relative aspect-[3/4] overflow-hidden rounded-2xl group shadow-xl shadow-black/30">
              <img 
                src="https://customer-assets.emergentagent.com/job_76fd0c54-e800-404c-90b2-29f2df9a4bf5/artifacts/1dtv1p1n_z-image_00030_.jpg" 
                alt="Artistic Boudoir Portrait - Emerald" 
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          </div>

          {/* CTA after Gallery */}
          <div className="mt-10 text-center">
            <Button 
              onClick={() => setShowConsultationModal(true)}
              className="bg-white/10 text-white hover:bg-white/20 border border-white/20 rounded-full px-8 py-6 text-base font-medium transition-all"
              data-testid="private-consultation-btn-gallery"
            >
              Request a Private Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Luxury Divider */}
      <div className="bg-[#050816] py-6 md:py-8">
        <div className="mx-auto max-w-[420px] px-6">
          <div className="h-px bg-[#C6A14A]/20" />
        </div>
      </div>

      {/* Romantic Description Section */}
      <section className="py-10 md:py-14 bg-[#050816]">
        <div className="container mx-auto px-6 max-w-[1100px]">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image - Golden Light Portrait */}
            <div className="relative aspect-square lg:aspect-[4/5] overflow-hidden rounded-2xl shadow-2xl shadow-black/40 order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_76fd0c54-e800-404c-90b2-29f2df9a4bf5/artifacts/el5f6nxs_z-image_00034_.jpg" 
                alt="Golden light boudoir portrait - sheer fabric and natural grace" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            {/* Description Text */}
            <div className="order-1 lg:order-2 lg:pl-4">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-px bg-[#C6A14A]/40" />
                <span className="text-[#C6A14A] text-xs font-medium tracking-[0.25em] uppercase">The Essence</span>
              </div>
              
              <p className="text-white/80 text-lg md:text-xl leading-relaxed font-light mb-6">
                Soft golden light settles across the room, tracing delicate lace and quiet movement as fabric shifts with the air. The moment feels unguarded yet composed — a study in silhouette, texture, and stillness. What remains is not simply an image, but a presence.
              </p>
              
              <p className="text-gray-400 text-base md:text-lg leading-relaxed font-light">
                Each Private Session unfolds as a collaboration between intuition and intention. Guided gently, composed deliberately, and captured with cinematic restraint, the result is imagery that feels timeless rather than temporary. Discreet. Refined. Enduring.
              </p>

              {/* Micro-CTA Link */}
              <button 
                onClick={() => setShowConsultationModal(true)}
                className="mt-6 text-[#C6A14A] hover:text-[#d4b65e] text-sm font-medium tracking-wide transition-colors inline-flex items-center gap-2 group"
                data-testid="private-consultation-link-essence"
              >
                Request a Private Consultation
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </button>

              <div className="flex items-center gap-3 mt-8">
                <div className="w-8 h-px bg-white/20" />
                <span className="text-white/30 text-xs tracking-widest uppercase">RC Studios</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Divider */}
      <div className="bg-[#050816] py-6 md:py-8">
        <div className="mx-auto max-w-[420px] px-6">
          <div className="h-px bg-[#C6A14A]/20" />
        </div>
      </div>

      {/* Expanded Services & Experience */}
      <section className="py-12 md:py-16 bg-[#050816]">
        <div className="container mx-auto px-6 max-w-[1000px]">
          <div className="grid md:grid-cols-2 gap-12">
            
            {/* Left Column: Services & Style */}
            <div className="space-y-10">
              <div>
                <h3 className="text-[#C6A14A] font-medium text-sm uppercase tracking-widest mb-6">Creative Focus</h3>
                <h2 className="text-3xl font-serif text-white mb-6">Artistic Portraiture</h2>
                <p className="text-gray-400 leading-relaxed font-light mb-8">
                  My work focuses on creating images that feel timeless, emotive, and visually striking. I specialize in a dark, moody aesthetic, using expert lighting to sculpt form and create atmosphere. This is fine art, not standard photography.
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-[#C6A14A] rounded-full"></span>
                    <span>Individual Artistic Sessions</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-[#C6A14A] rounded-full"></span>
                    <span>Couples & Intimate Portraiture</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-[#C6A14A] rounded-full"></span>
                    <span>Conceptual & Editorial Projects</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-[#C6A14A] rounded-full"></span>
                    <span>Private Wedding Commissions</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-[#C6A14A] font-medium text-sm uppercase tracking-widest mb-6">Experience</h3>
                <p className="text-gray-400 leading-relaxed font-light">
                  With over 7 years of experience in private and artistic photography, I understand the nuances of working in personal spaces. I bring the same level of technical precision and professionalism to these sessions as I do to my commercial work, ensuring a comfortable, guided experience from start to finish.
                </p>
              </div>
            </div>

            {/* Right Column: Pricing & Portfolio Offer */}
            <div className="space-y-8">
              <div className="bg-white/5 border border-white/10 p-8 rounded-2xl">
                <h3 className="text-white font-serif text-2xl mb-4">Investment</h3>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  Private sessions are fully customized experiences. Pricing reflects the time, technical expertise, and detailed retouching required for fine-art delivery.
                </p>
                <div className="space-y-4">
                  <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                    <span className="text-gray-300 font-medium">Portrait Sessions</span>
                    <span className="text-[#C6A14A]">From $500</span>
                  </div>
                  <div className="flex justify-between items-baseline border-b border-white/10 pb-4">
                    <span className="text-gray-300 font-medium">Couples & Concepts</span>
                    <span className="text-[#C6A14A]">From $750</span>
                  </div>
                  <div className="flex justify-between items-baseline pb-2">
                    <span className="text-gray-300 font-medium">Weddings</span>
                    <span className="text-[#C6A14A]">Custom Quote</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#C6A14A]/10 to-transparent border border-[#C6A14A]/20 p-8 rounded-2xl relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-[#C6A14A] font-serif text-xl mb-3">Portfolio Opportunity</h3>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    I am currently expanding my portfolio in this genre. For select clients willing to allow limited, discreet use of images for advertising (faces hidden or specific artistic crops only), I offer preferred rates on session fees.
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    *Available by application and discussion only.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* CTA after Services/Pricing */}
          <div className="mt-12 text-center">
            <Button 
              onClick={() => setShowConsultationModal(true)}
              className="bg-white/10 text-white hover:bg-white/20 border border-white/20 rounded-full px-8 py-6 text-base font-medium transition-all"
              data-testid="private-consultation-btn-pricing"
            >
              Request a Private Consultation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivateSessions;
